/**
 * Configuration variables for Changelog behavior
 */
const CONFIG = {
    // Distance in pixels before reaching the sentinel to start pre-fetching the next page
    FETCH_THRESHOLD_PX: 400,
    SEARCH_DEBOUNCE_MS: 300,
};

class Versions {
    constructor() {
        this.fetchController = null;
        this.paginationController = null;
        this.observer = null;
        this.isLoadingMore = false;
        this.requestGeneration = 0;
        this.searchTimer = null;

        this.initSelectors();
        this.query = this.searchInput?.value.trim() || '';
        this.activeSort =
            this.sortPopover?.querySelector('[role="option"][aria-selected="true"]')?.dataset.value ||
            'newest';
        this.activeYear = this.allYearsOption?.getAttribute('aria-selected') === 'true'
            ? null
            : this.yearPopover?.querySelector('.year-grid button[aria-pressed="true"]')?.dataset.value || null;
        this.bindEvents();
        this.initObserver();
    }

    initSelectors() {
        this.wrapper = document.getElementById('changelog-tags-wrapper');
        this.changelog = document.querySelector('.changelog');
        this.changelogList = document.querySelector('.changelog-list');
        this.searchInput = document.getElementById('changelog-search-input');
        this.clearSearchBtn = document.querySelector('.changelog-controls__search .icon');
        this.status = document.getElementById('changelog-status');
        this.sentinel = document.getElementById('changelog-sentinel');
        this.versionTemplate = document.getElementById('changelog-version-template');
        this.groupTemplate = document.getElementById('changelog-group-template');
        this.sortPopover = document.getElementById('sort-popover');
        this.sortTrigger = document.getElementById('select-trigger');
        this.activeSortIcon = document.getElementById('active-icon');
        this.selectedSortOption = document.getElementById('selected-option');
        this.sortOptions = this.sortPopover?.querySelectorAll('[role="option"]');
        this.yearPopover = document.getElementById('year-popover');
        this.yearTrigger = document.getElementById('year-trigger');
        this.selectedYearLabel = document.getElementById('selected-year');
        this.yearOptions = this.yearPopover?.querySelectorAll('.year-grid button');
        this.allYearsOption = document.getElementById('btn-all-years');
    }

    bindEvents() {
        if (this.wrapper) {
            this.wrapper.addEventListener('command', (e) => this.handleCommand(e));
            this.wrapper.addEventListener('click', (e) => this.handleTagClick(e));
        }

        if (this.changelog) {
            this.changelog.addEventListener('click', (e) => this.handleChangelogClick(e));
        }

        this.searchInput?.addEventListener('input', () => this.handleSearchInput());
        this.clearSearchBtn?.addEventListener('click', () => this.handleClearSearch());
		this.sortPopover.addEventListener('toggle', (e) => this.handleSortToggle(e));

        this.sortOptions?.forEach((option) => {
            option.addEventListener('click', () => this.handleSortOption(option));
        });

		this.yearPopover.addEventListener('toggle', (e) => this.handleYearToggle(e));

        this.yearOptions?.forEach((option) => {
            option.addEventListener('click', () => this.handleYearOption(option));
        });

        this.allYearsOption?.addEventListener('click', () => this.handleAllYearsOption());
    }

    positionSortPopoverFallback() {
        if (!CSS.supports('top', 'anchor(bottom)')) {
            const rect = this.sortTrigger.getBoundingClientRect();
            this.sortPopover.style.top = `${rect.bottom + window.scrollY + 6}px`;
            this.sortPopover.style.left = `${rect.left + window.scrollX}px`;
        }
    }

    handleSortToggle(e) {
        const isOpen = e.newState === 'open';
        this.sortTrigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

        if (isOpen) {
            this.positionSortPopoverFallback();
        }
    }

    handleSortOption(option) {
        this.sortOptions.forEach((opt) => opt.setAttribute('aria-selected', 'false'));
        option.setAttribute('aria-selected', 'true');

        const labelSpan = option.querySelector('span');
        if (labelSpan) {
            this.selectedSortOption.textContent = labelSpan.textContent;
        }

        const optionIcon = option.querySelector('svg');
        if (optionIcon) {
            this.activeSortIcon.innerHTML = '';
            this.activeSortIcon.appendChild(optionIcon.cloneNode(true));
        }

        this.sortPopover.hidePopover();
        this.activeSort = option.dataset.value || 'newest';
        void this.fetchFilteredChangelog();
    }

    positionYearPopoverFallback() {
        if (!CSS.supports('top', 'anchor(bottom)')) {
            const rect = this.yearTrigger.getBoundingClientRect();
            this.yearPopover.style.top = `${rect.bottom + window.scrollY + 6}px`;
            this.yearPopover.style.left = `${rect.right + window.scrollX - this.yearPopover.offsetWidth}px`;
        }
    }

    handleYearToggle(e) {
        const isOpen = e.newState === 'open';
        this.yearTrigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

        if (isOpen) {
            this.positionYearPopoverFallback();
        }
    }

    setSelectedYear(value, label, isAll = false) {
        this.yearOptions.forEach((option) => option.setAttribute('aria-pressed', 'false'));
        this.allYearsOption.setAttribute('aria-selected', isAll ? 'true' : 'false');

        if (!isAll) {
            const activeOption = this.yearPopover.querySelector(
                `.year-grid button[data-value="${value}"]`
            );
            activeOption?.setAttribute('aria-pressed', 'true');
        }

        this.selectedYearLabel.textContent = label;
        this.yearPopover.hidePopover();
    }

    handleYearOption(option) {
        const value = option.dataset.value;
        this.activeYear = value || null;
        this.setSelectedYear(value, value);
        void this.fetchFilteredChangelog();
    }

    handleAllYearsOption() {
        this.activeYear = null;
        this.setSelectedYear('all', 'Sve godine', true);
        void this.fetchFilteredChangelog();
    }

    handleSearchInput() {
        this.query = this.searchInput.value.trim();
        this.cancelPendingRequests();

        this.searchTimer = window.setTimeout(() => {
            this.searchTimer = null;
            void this.fetchFilteredChangelog();
        }, CONFIG.SEARCH_DEBOUNCE_MS);
    }

    handleClearSearch() {
        if (!this.searchInput) return;

        const hadValue = Boolean(this.searchInput.value || this.query);
        this.searchInput.value = '';
        this.query = '';
        this.cancelPendingRequests();

        if (hadValue) {
            void this.fetchFilteredChangelog();
        }

        this.searchInput.focus();
    }

    // ──────────────────────────────────────────────────────────────────────────
    // Template Rendering Engine
    // ──────────────────────────────────────────────────────────────────────────

    createVersionElement(version, index) {
        if (!this.versionTemplate) return null;

        const fragment = this.versionTemplate.content.cloneNode(true);
        const article = fragment.querySelector('article');

        article.dataset.versionId = version.id || '0';

        const titleEl = article.querySelector('[data-version-title]');
        if (titleEl) {
            titleEl.textContent = `Verzija ${version.version || ''}`;
        }

        const dateEl = article.querySelector('[data-version-date]');
        if (dateEl && version.release_date) {
            const date = String(version.release_date);
            dateEl.setAttribute('datetime', date);
            this.renderHighlightedText(
                dateEl,
                this.formatReleaseDate(date),
                this.getHighlightRanges(version, 'release_date')
            );
        }

        const numberEl = article.querySelector('[data-version-number]');
        if (numberEl) {
            this.renderHighlightedText(
                numberEl,
                version.version || '',
                this.getHighlightRanges(version, 'version')
            );
        }

        const countLabelEl = article.querySelector('[data-version-count-label]');
        if (countLabelEl) {
            countLabelEl.textContent = this.formatCountLabel(
                version.total_count,
                version.filtered_count
            );
        }

        const toggleBtn = article.querySelector('[data-version-expand]');
        if (toggleBtn) {
            const totalCount = parseInt(version.total_count, 10) || 0;
            const filteredCount =
                version.filtered_count !== undefined && version.filtered_count !== null
                    ? parseInt(version.filtered_count, 10)
                    : null;

            if (filteredCount !== null && filteredCount < totalCount) {
                toggleBtn.dataset.versionId = String(version.id);
                toggleBtn.dataset.filteredCount = String(filteredCount);
                toggleBtn.dataset.totalCount = String(totalCount);
                toggleBtn.textContent = `Prikaži sve (${totalCount})`;
                toggleBtn.hidden = false;
            } else {
                toggleBtn.hidden = true;
            }
        }

        const markerEl = article.querySelector('.changelog-marker');
        if (markerEl) {
            if (index === 0) {
                const staticMarker = document.createElement('span');
                staticMarker.className = 'changelog-marker';
                staticMarker.setAttribute('aria-hidden', 'true');
                markerEl.replaceWith(staticMarker);
            } else {
                markerEl.setAttribute('aria-label', `Skoči na verziju ${version.version || ''}`);
            }
        }

        const groupsContainer = article.querySelector('[data-version-groups]');
        if (groupsContainer && version.grouped_items) {
            article._filteredGroupsData = version.grouped_items;
            this.renderGroups(version.grouped_items, groupsContainer);
        }

        return fragment;
    }

    formatReleaseDate(date) {
        return date.split('-').reverse().join('.') + '.';
    }

    getHighlightRanges(source, field) {
        if (!source || typeof source !== 'object') return [];

        return source.highlight_ranges?.[field]
            || source.highlights?.[field]
            || (Array.isArray(source.highlights) && source.highlights)
            || source[`${field}_highlight_ranges`]
            || [];
    }

    normalizeHighlightRanges(value, ranges) {
        if (!Array.isArray(ranges) || typeof value !== 'string') return [];

        return ranges
            .map((range) => {
                let start;
                let end;

                if (Array.isArray(range)) {
                    [start, end] = range;
                } else if (range && typeof range === 'object') {
                    start = range.start ?? range.offset;
                    end = range.end ?? (Number.isInteger(range.length) ? start + range.length : undefined);
                }

                if (!Number.isInteger(start) || !Number.isInteger(end)) return null;

                return {
                    start: Math.max(0, start),
                    end: Math.min(value.length, end),
                };
            })
            .filter((range) => range && range.end > range.start)
            .sort((first, second) => first.start - second.start || first.end - second.end)
            .reduce((normalized, range) => {
                const previous = normalized[normalized.length - 1];

                if (previous && range.start <= previous.end) {
                    previous.end = Math.max(previous.end, range.end);
                } else {
                    normalized.push(range);
                }

                return normalized;
            }, []);
    }

    renderHighlightedText(element, value, ranges = []) {
        const text = String(value ?? '');
        const normalizedRanges = this.normalizeHighlightRanges(text, ranges);
        const fragment = document.createDocumentFragment();
        let cursor = 0;

        normalizedRanges.forEach(({ start, end }) => {
            if (start > cursor) {
                fragment.append(document.createTextNode(text.slice(cursor, start)));
            }

            const mark = document.createElement('mark');
            mark.textContent = text.slice(start, end);
            fragment.append(mark);
            cursor = end;
        });

        if (cursor < text.length || normalizedRanges.length === 0) {
            fragment.append(document.createTextNode(text.slice(cursor)));
        }

        element.replaceChildren(fragment);
    }

    renderGroups(groupedItems, container) {
        if (!this.groupTemplate || !container) return;

        container.replaceChildren();
        const fragment = document.createDocumentFragment();

        Object.entries(groupedItems).forEach(([tagName, group]) => {
            const groupFragment = this.groupTemplate.content.cloneNode(true);

            const badge = groupFragment.querySelector('[data-group-badge]');
            if (badge) {
                badge.textContent = tagName;
                if (group.color) {
                    badge.style.setProperty('--badge-clr', group.color);
                }
            }

            const itemsList = groupFragment.querySelector('[data-group-items]');
            if (itemsList && Array.isArray(group.items)) {
                group.items.forEach((item) => {
                    const li = document.createElement('li');
                    const body = typeof item === 'object' && item !== null ? item.body : item;
                    this.renderHighlightedText(
                        li,
                        body,
                        this.getHighlightRanges(item, 'body')
                    );
                    itemsList.appendChild(li);
                });
            }

            fragment.appendChild(groupFragment);
        });

        container.appendChild(fragment);
    }

    // ──────────────────────────────────────────────────────────────────────────
    // Infinite Scroll Observer
    // ──────────────────────────────────────────────────────────────────────────

    initObserver() {
        if (!this.sentinel) return;

        const observerOptions = {
            root: null,
            rootMargin: `0px 0px ${CONFIG.FETCH_THRESHOLD_PX}px 0px`,
            threshold: 0,
        };

        this.observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    this.handleSentinelIntersection();
                }
            });
        }, observerOptions);

        this.observeSentinel();
    }

    observeSentinel() {
        if (!this.sentinel || !this.observer) return;

        const hasMore = this.sentinel.dataset.hasMore === 'true';
        if (hasMore) {
            this.observer.observe(this.sentinel);
        }
    }

    unobserveSentinel() {
        if (this.sentinel && this.observer) {
            this.observer.unobserve(this.sentinel);
        }
    }

    getActiveTagSlugs() {
        return Array.from(
            this.wrapper?.querySelectorAll('[data-tag-filter][aria-pressed="true"]') || []
        ).map((badge) => badge.dataset.tagFilter);
    }

    buildChangelogRequest(paged) {
        const activeSlugs = this.getActiveTagSlugs();
        const params = new URLSearchParams({
            action: 'w4d_get_frontend_changelog',
            _nonce: w4d_frontend.nonce,
            paged: String(paged),
            sort: this.activeSort,
        });

        activeSlugs.forEach((slug) => params.append('tags[]', slug));

        if (this.activeYear) {
            params.set('year', this.activeYear);
        }

        if (this.query) {
            params.set('query', this.query);
        }

        return { params, activeSlugs };
    }

    cancelPendingRequests() {
        if (this.searchTimer) {
            window.clearTimeout(this.searchTimer);
            this.searchTimer = null;
        }

        this.unobserveSentinel();
        this.requestGeneration += 1;
        this.fetchController?.abort();
        this.paginationController?.abort();
        this.fetchController = null;
        this.paginationController = null;
        this.isLoadingMore = false;
    }

    async requestJson(params, signal) {
        const response = await fetch(`${w4d_frontend.url}?${params.toString()}`, { signal });

        if (!response.ok) {
            throw new Error(`Changelog request failed with status ${response.status}`);
        }

        let result;
        try {
            result = await response.json();
        } catch {
            throw new Error('Changelog request did not return valid JSON');
        }

        return result;
    }

    async requestChangelog(params, signal) {
        const result = await this.requestJson(params, signal);

        if (!result || result.success !== true || !result.data || !Array.isArray(result.data.versions)) {
            throw new Error('Changelog request returned an invalid response');
        }

        return result;
    }

    updateStatus(message, isError = false) {
        if (!this.status) return;

        this.status.textContent = message;
        this.status.hidden = !message;
        this.status.toggleAttribute('data-error', isError);
    }

    handleSentinelIntersection() {
        if (this.isLoadingMore) return;

        const hasMore = this.sentinel.dataset.hasMore === 'true';
        if (!hasMore) {
            this.unobserveSentinel();
            return;
        }

        void this.fetchNextPage();
    }

    async fetchNextPage() {
        this.isLoadingMore = true;

        const currentPaged = parseInt(this.sentinel.dataset.paged, 10) || 1;
        const nextPage = currentPaged + 1;
        const generation = this.requestGeneration;
        const controller = new AbortController();
        const { params } = this.buildChangelogRequest(nextPage);
        this.paginationController = controller;

        try {
            const result = await this.requestChangelog(params, controller.signal);

            if (generation !== this.requestGeneration) return;

            if (result.success && Array.isArray(result.data.versions) && result.data.versions.length > 0) {
                const currentCount = this.changelogList.querySelectorAll('article').length;
                const fragment = document.createDocumentFragment();

                result.data.versions.forEach((version, idx) => {
                    fragment.appendChild(this.createVersionElement(version, currentCount + idx));
                });

                this.changelogList.appendChild(fragment);

                this.sentinel.dataset.paged = String(result.data.paged);
                this.sentinel.dataset.hasMore = String(result.data.has_more);

                if (!result.data.has_more) {
                    this.unobserveSentinel();
                }

                this.updateStatus('Učitane su dodatne izmjene.');
            } else {
                this.sentinel.dataset.hasMore = 'false';
                this.unobserveSentinel();
                this.updateStatus('Nema više izmjena.');
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error('Error loading next changelog page:', error);
                this.updateStatus('Dodatne izmjene trenutačno nisu dostupne. Prikazani rezultati nisu promijenjeni.', true);
            }
        } finally {
            if (this.paginationController === controller) {
                this.paginationController = null;
            }
            if (generation === this.requestGeneration) {
                this.isLoadingMore = false;
            }
        }
    }

    // ──────────────────────────────────────────────────────────────────────────
    // Click Delegations (Jump & Expansion)
    // ──────────────────────────────────────────────────────────────────────────

    handleChangelogClick(e) {
        if (e.target.closest('[data-changelog-jump]')) {
            this.handleJumpClick(e);
            return;
        }

        if (e.target.closest('[data-version-expand]')) {
            this.handleVersionExpand(e);
            return;
        }
    }

    handleJumpClick(e) {
        const button = e.target.closest('[data-changelog-jump]');
        if (!button) return;

        const article = button.closest('article');
        if (!article) return;

        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        article.scrollIntoView({
            behavior: prefersReducedMotion ? 'auto' : 'smooth',
            block: 'start',
        });
    }

    async handleVersionExpand(e) {
        const button = e.target.closest('[data-version-expand]');
        if (!button) return;

        const article = button.closest('article');
        if (!article) return;

        const groupsContainer = article.querySelector('[data-version-groups]');
        if (!groupsContainer) return;

        const isExpanded = button.getAttribute('aria-expanded') === 'true';
        const versionId = button.dataset.versionId;
        const filteredCount = button.dataset.filteredCount;
        const totalCount = button.dataset.totalCount;

        if (isExpanded) {
            if (article._filteredGroupsData) {
                this.renderGroups(article._filteredGroupsData, groupsContainer);
            }
            button.setAttribute('aria-expanded', 'false');
            button.textContent = `Prikaži sve (${totalCount})`;
            article.classList.remove('is-fully-expanded');
            return;
        }

        if (article._allGroupsData) {
            this.renderGroups(article._allGroupsData, groupsContainer);
            button.setAttribute('aria-expanded', 'true');
            button.textContent = `Prikaži samo filtrirano (${filteredCount})`;
            article.classList.add('is-fully-expanded');
            return;
        }

        button.disabled = true;
        const originalText = button.textContent;
        button.textContent = 'Učitavanje...';

        const params = new URLSearchParams({
            action: 'w4d_get_version_items',
            _nonce: w4d_frontend.nonce,
            version_id: versionId,
        });

        try {
            const result = await this.requestJson(params);

            if (!result || result.success !== true || !result.data?.grouped_items) {
                throw new Error('Version items request returned an invalid response');
            }

            article._allGroupsData = result.data.grouped_items;
            this.renderGroups(result.data.grouped_items, groupsContainer);
            button.setAttribute('aria-expanded', 'true');
            button.textContent = `Prikaži samo filtrirano (${filteredCount})`;
            article.classList.add('is-fully-expanded');
        } catch (error) {
            console.error('Error fetching version items:', error);
            button.textContent = originalText;
        } finally {
            button.disabled = false;
        }
    }

    // ──────────────────────────────────────────────────────────────────────────
    // Tag Filtering Logic
    // ──────────────────────────────────────────────────────────────────────────

    handleCommand(e) {
        if (e.command === '--toggle-tags') {
            const button = e.source;
            const isExpanded = button.getAttribute('aria-expanded') === 'true';

            button.setAttribute('aria-expanded', String(!isExpanded));
        }

        if (e.command === '--clear-tags') {
            this.clearAllTags();
        }
    }

    clearAllTags() {
        const activeBadges = this.wrapper.querySelectorAll('[data-tag-filter][aria-pressed="true"]');
        activeBadges.forEach((badge) => badge.setAttribute('aria-pressed', 'false'));

        this.updateCount();
        void this.fetchFilteredChangelog();
    }

    handleTagClick(e) {
        const badge = e.target.closest('[data-tag-filter]');
        if (!badge) return;

        const isPressed = badge.getAttribute('aria-pressed') === 'true';
        badge.setAttribute('aria-pressed', String(!isPressed));

        this.updateCount();
        void this.fetchFilteredChangelog();
    }

    updateCount() {
        const counter = this.wrapper.querySelector('header h3');
        const activeCount = this.wrapper.querySelectorAll('[data-tag-filter][aria-pressed="true"]').length;

        if (counter) {
            counter.dataset.count = String(activeCount);
        }
    }

    async fetchFilteredChangelog() {
        this.cancelPendingRequests();
        const generation = this.requestGeneration;

        const controller = new AbortController();
        const { params, activeSlugs } = this.buildChangelogRequest(1);
        this.fetchController = controller;

        this.changelog.classList.add('is-loading');
        this.changelog.classList.remove('is-loaded');
        this.updateStatus(this.query ? 'Pretraživanje izmjena...' : 'Učitavanje izmjena...');

        try {
            const result = await this.requestChangelog(params, controller.signal);

            if (generation !== this.requestGeneration) return;

            if (result.success) {
                this.changelogList.replaceChildren();

                if (Array.isArray(result.data.versions) && result.data.versions.length > 0) {
                    const fragment = document.createDocumentFragment();

                    result.data.versions.forEach((version, idx) => {
                        fragment.appendChild(this.createVersionElement(version, idx));
                    });

                    this.changelogList.appendChild(fragment);
                } else {
                    const emptyMsg = document.createElement('p');
                    emptyMsg.className = 'changelog-empty';
                    emptyMsg.textContent = 'Nema pronađenih izmjena za odabrane filtre.';
                    this.changelogList.appendChild(emptyMsg);
                }

                if (this.sentinel) {
                    this.sentinel.dataset.paged = '1';
                    this.sentinel.dataset.hasMore = String(result.data.has_more);
                    this.sentinel.dataset.tags = activeSlugs.join(',');
                    this.sentinel.dataset.sort = this.activeSort;
                    this.sentinel.dataset.year = this.activeYear || 'all';
                    this.sentinel.dataset.query = this.query;
                }

                this.observeSentinel();
                this.updateStatus(
                    result.data.search_source === 'fallback'
                        ? 'Prikazani su rezultati iz zamjenske pretrage.'
                        : result.data.versions.length > 0
                            ? 'Izmjene su ažurirane.'
                            : 'Nema pronađenih izmjena za odabrane filtre.'
                );
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error('Error fetching changelog:', error);
                this.updateStatus('Izmjene trenutačno nisu dostupne. Prikazani rezultati nisu promijenjeni.', true);
            }
        } finally {
            if (this.fetchController === controller) {
                this.fetchController = null;
            }
            if (generation === this.requestGeneration) {
                this.changelog.classList.remove('is-loading');
                this.changelog.classList.add('is-loaded');
            }
        }
    }
    
    declension(count, one, few, many) {
        const mod100 = count % 100;
        const mod10 = count % 10;
    
        if (mod100 >= 11 && mod100 <= 14) {
            return many;
        }
        if (mod10 === 1) {
            return one;
        }
        if (mod10 >= 2 && mod10 <= 4) {
            return few;
        }
        return many;
    }
    
    formatCountLabel(totalCount, filteredCount = null) {
        const totalLabel = this.declension(totalCount, 'izmjena', 'izmjene', 'izmjena');
    
        if (filteredCount !== null && filteredCount !== undefined) {
            const prefix = filteredCount === 1 ? 'Prikazana' : 'Prikazano';
            return `${prefix} ${filteredCount} od ${totalCount} ${totalLabel}`;
        }
    
        return `${totalCount} ${totalLabel}`;
    }
}

document.addEventListener('DOMContentLoaded', () => {
    new Versions();
});